import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { storageLocationsApi, StorageLocationType } from '@/api/services/yard.api';
import { locationsApi, Location } from '@/api/services/locations.api';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const storageTypes: StorageLocationType[] = [
  'RACK',
  'BIN',
  'FLOOR',
  'OUTDOOR',
  'TANK',
  'PIPE_RACK',
  'CONTAINER',
];

export default function YardStorageLocationsNew() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    locationId: '',
    code: '',
    name: '',
    type: 'RACK' as StorageLocationType,
    zone: '',
    aisle: '',
    rack: '',
    level: '',
    bin: '',
    maxWeight: '',
    maxVolume: '',
  });

  useEffect(() => {
    locationsApi
      .getAll({})
      .then((res) => setLocations(res.data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.locationId || !formData.code || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      await storageLocationsApi.create({
        locationId: formData.locationId,
        code: formData.code,
        name: formData.name,
        type: formData.type,
        zone: formData.zone || undefined,
        aisle: formData.aisle || undefined,
        rack: formData.rack || undefined,
        level: formData.level || undefined,
        bin: formData.bin || undefined,
        maxWeight: formData.maxWeight ? parseFloat(formData.maxWeight) : undefined,
        maxVolume: formData.maxVolume ? parseFloat(formData.maxVolume) : undefined,
      });
      toast.success('Storage location created');
      navigate('/yard/locations');
    } catch (error) {
      toast.error('Failed to create storage location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Add Storage Location"
        description="Code, zone, and parent location required to register a storage bin."
        actions={
          <Button variant="outline" onClick={() => navigate('/yard/locations')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Storage Locations
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Location Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Location Details</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="locationId">Site Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select site location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="RACK-A1-01"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Rack A1 Bay 1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(v: StorageLocationType) => setFormData({ ...formData, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {storageTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Position */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Position (Optional)</h3>
            <Separator />

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zone">Zone</Label>
                <Input
                  id="zone"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  placeholder="A"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aisle">Aisle</Label>
                <Input
                  id="aisle"
                  value={formData.aisle}
                  onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                  placeholder="01"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rack">Rack</Label>
                <Input
                  id="rack"
                  value={formData.rack}
                  onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                  placeholder="A1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="level">Level</Label>
                <Input
                  id="level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  placeholder="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bin">Bin</Label>
                <Input
                  id="bin"
                  value={formData.bin}
                  onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                  placeholder="01"
                />
              </div>
            </div>
          </div>

          {/* Capacity */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Capacity (Optional)</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="maxWeight">Max Weight (kg)</Label>
                <Input
                  id="maxWeight"
                  type="number"
                  value={formData.maxWeight}
                  onChange={(e) => setFormData({ ...formData, maxWeight: e.target.value })}
                  placeholder="5000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxVolume">Max Volume (m³)</Label>
                <Input
                  id="maxVolume"
                  type="number"
                  step="0.01"
                  value={formData.maxVolume}
                  onChange={(e) => setFormData({ ...formData, maxVolume: e.target.value })}
                  placeholder="10.00"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/yard/locations')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Storage Location'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
